/**
 * AppsHome — My apps, at /apps.
 *
 * The apps this person keeps: a small starter set until they change it, then
 * whatever they added from the catalog (/apps/catalog) and did not take off
 * (src/apps/myApps.ts; choices kept by get_my_apps / set_my_app, 1064). The
 * catalog is every app they may open — absence, not disablement (C Mode rule
 * 2) — so a kept app they may not open here is simply not drawn.
 *
 * The apps sit in one grid, in catalog order, like a home screen; the
 * catalog is where they are grouped by what they are for. The page sits
 * inside the product shell so the header and the way home are the same as everywhere else
 * (owner, 2026-10-07: it was a bare column with no header and no way back).
 *
 * Where the words come from: every card's name and tagline is the app's
 * entry in src/apps/registry.ts.
 */

import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Plus, Sparkles } from 'lucide-react';
import { APP_ACCENT_CLASSES, type AppDefinition } from './registry';
import { isLauncherApp, WORKSPACE_TYPE_LABELS } from './appsHomeSections';
import { useMyApps } from './myApps';
import { AppsNav } from './AppsNav';
import { AppCard, EmptyState, PrimaryButton } from './components/primitives';
import { useMobileExperience } from '../mobile/featureFlag';
import { useWorkspaceType } from '@/lib/workspaceModulesService';

function LauncherCard({ app }: { app: AppDefinition }) {
  const navigate = useNavigate();
  const accent = APP_ACCENT_CLASSES[app.accent];
  return (
    <AppCard onClick={() => navigate(app.routeFamily)} className="h-full">
      <div className="flex h-full items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-heading font-semibold text-base ${accent.chipBg} ${accent.chipText}`}
        >
          {app.name.charAt(0)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-[var(--text-pri)]">{app.name}</p>
          <p className="mt-0.5 font-body text-sm text-[var(--text-sec)]">{app.tagline}</p>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
      </div>
    </AppCard>
  );
}

export default function AppsHome() {
  const navigate = useNavigate();
  const isMobile = useMobileExperience();
  const { ready, catalog, myApps } = useMyApps();
  const workspaceType = useWorkspaceType();
  // Start is the front door, not a peer app — render it as the hero, not a card.
  const startApp = catalog.find((app) => app.appId === 'start');
  const launcherApps = myApps.filter((app) => isLauncherApp(app, isMobile));
  const waiting = catalog.filter((app) => isLauncherApp(app, isMobile)).length - launcherApps.length;

  return (
    <div className="mx-auto w-full max-w-5xl">
      <AppsNav
        title="My apps"
        lede="Small, focused apps on the same record. Each one does one job — keep the ones you use."
      >
        {workspaceType ? (
          <p className="mt-1 max-w-lg font-body text-sm text-[var(--text-ter)]">
            Showing your apps offered in {WORKSPACE_TYPE_LABELS[workspaceType]} workspaces.
          </p>
        ) : null}
      </AppsNav>
      {startApp ? (
        <button
          type="button"
          onClick={() => navigate(startApp.routeFamily)}
          className="mt-6 flex w-full items-center gap-4 border border-[var(--rule)] bg-[var(--bg-raised)] p-5 text-left hover:bg-[var(--hover-tint)]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--slate-dim)] text-[var(--text-sec)]">
            <Sparkles className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading font-semibold text-[var(--text-pri)]">What are you trying to do?</span>
            <span className="mt-0.5 block font-body text-sm text-[var(--text-sec)]">{startApp.tagline}</span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
        </button>
      ) : null}
      {ready && launcherApps.length > 0 ? (
        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {launcherApps.map((app) => (
            <LauncherCard key={app.appId} app={app} />
          ))}
        </div>
      ) : null}
      {ready && launcherApps.length > 0 && waiting > 0 ? (
        <Link
          to="/apps/catalog"
          className="mt-3 flex w-full items-center gap-4 border border-dashed border-[var(--rule-bright)] p-5 text-left hover:bg-[var(--hover-tint)]"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--rule)] text-[var(--text-sec)]">
            <Plus className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading font-semibold text-[var(--text-pri)]">Add apps</span>
            <span className="mt-0.5 block font-body text-sm text-[var(--text-sec)]">
              {waiting === 1 ? '1 more app is' : `${waiting} more apps are`} in the catalog. Add or take off any app there.
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
        </Link>
      ) : null}
      {ready && launcherApps.length === 0 && waiting > 0 ? (
        <div className="mt-8">
          <EmptyState
            title="Nothing in My apps yet"
            hint="Pick the apps you want from the catalog. They stay here, on every device you sign in on."
            action={<PrimaryButton onClick={() => navigate('/apps/catalog')}>Browse the catalog</PrimaryButton>}
          />
        </div>
      ) : null}
      {ready && catalog.length === 0 ? (
        <div className="mt-2">
          <EmptyState
            title="No apps are available to you here yet"
            hint="Ask a workspace admin for access."
          />
        </div>
      ) : null}
    </div>
  );
}
